import {
  type PropsWithChildren,
  type RefObject,
  useLayoutEffect,
  useRef,
} from 'react';

import {
  cleanupStreamLainDropdownPanel,
  decorateStreamLainDropdownPanel,
  type StreamLainDropdownAnchor,
} from './chrome';

/**
 * Applies the StreamLain dropdown panel chrome (tail pointing at the trigger
 * plus the grow-out-of-the-tail enter/exit animation) to the closest Radix
 * menu/popover content element.
 *
 * Render this as direct child of the Radix content, wrapping the actual panel
 * content. The panel side is taken from Radix' `data-side` after positioning:
 * `left`/`right` place the tail on the vertical edge, `top`/`bottom` keep the
 * original vertical tail auto-placement. The exit animation runs via the
 * `[data-state='closed']` rules in the chrome stylesheet; Radix delays
 * unmounting until it finishes.
 */
export const RadixDropdownPanelChrome = ({
  children,
  triggerRef,
}: PropsWithChildren<{
  triggerRef: RefObject<StreamLainDropdownAnchor | null>;
}>) => {
  const anchorRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const content = anchorRef.current?.closest<HTMLElement>(
      '[data-radix-menu-content], [data-radix-popper-content-wrapper] > [data-state]'
    );
    const trigger = triggerRef.current;
    if (!content || !trigger) return;

    // Hide until the tail is positioned; decorate manages opacity from here.
    content.style.opacity = '0';
    // Defer one frame so Radix' popper layout effect (parent, runs after this
    // child effect) has positioned the content and settled `data-side`.
    const frame = requestAnimationFrame(() => {
      const dataSide = content.getAttribute('data-side');
      decorateStreamLainDropdownPanel(
        content,
        trigger,
        dataSide === 'left' || dataSide === 'right'
          ? { side: dataSide }
          : undefined
      );
    });
    return () => {
      cancelAnimationFrame(frame);
      cleanupStreamLainDropdownPanel(content);
    };
  }, [triggerRef]);

  return (
    <>
      <span ref={anchorRef} style={{ display: 'none' }} aria-hidden="true" />
      {children}
    </>
  );
};
